import { calculateIncomeTax, incomeTaxRulesFor } from "../income-tax/index.js";
import type { FinancialYear } from "../types.js";
import {
  type GstThresholdInput,
  GstThresholdInputSchema,
  type GstThresholdOutput,
  type RideshareGstInput,
  RideshareGstInputSchema,
  type RideshareGstOutput,
  type SetAsideInput,
  SetAsideInputSchema,
  type SetAsideOutput,
  type SideIncomeRuleSet,
} from "./types.js";

// ---------------------------------------------------------------------------
// Pure calculation functions for the side-income surface. No I/O, no
// Date.now(); the assessment FY (and so the ruleset) is always a parameter.
//
// The set-aside face composes the shared `income-tax` engine for marginal
// stacking rather than re-deriving bracket math — `tax(salary + abn)` minus
// `tax(salary)` is the EXTRA tax the side income causes, taxed on top of the
// salary at the salary's marginal rate (never the side income taxed in
// isolation, the error free calculators routinely make).
// ---------------------------------------------------------------------------

/** Conservative set-aside step. We round the effective rate UP to the next 5%
 * so the per-invoice guidance never leaves the user short at EOFY (regulatory
 * ship-condition: round up, frame as an estimate). */
const SET_ASIDE_STEP = 0.05;

/** Total tax (income tax + Medicare levy + compulsory HELP repayment) for a
 * resident on `taxableIncome`, via the shared income-tax engine. Side income
 * stacks on salary, so HELP repayment income == taxable income here (the side
 * income lifts both together). */
function totalTaxFor(
  taxableIncome: number,
  hasStudyLoan: boolean,
  fy: FinancialYear,
): { tax: number; marginalRate: number } {
  const rules = incomeTaxRulesFor(fy);
  const out = calculateIncomeTax(
    {
      taxableIncome,
      residency: "resident",
      hasStudyLoan,
      assessmentYear: fy,
    },
    rules,
  );
  // netTax already = income tax (after LITO/SAPTO) + Medicare levy; add the
  // compulsory HELP repayment, which is the rest of the EOFY cash obligation.
  return { tax: out.netTax + out.helpRepayment, marginalRate: out.marginalRate };
}

/**
 * Set-aside: the EXTRA tax a salaried person owes on expected ABN income,
 * marginally stacked, plus the two next-year auto-enrolment flags. Pure.
 */
export function calculateSetAside(
  rawInput: SetAsideInput,
  rules: SideIncomeRuleSet,
): SetAsideOutput {
  const input = SetAsideInputSchema.parse(rawInput);
  const fy = input.assessmentYear as FinancialYear;

  const baseline = totalTaxFor(input.salary, input.hasStudyLoan, fy);
  const combined = totalTaxFor(
    input.salary + input.abnIncome,
    input.hasStudyLoan,
    fy,
  );

  // Extra tax = combined − salary baseline. Clamp at 0: a stacking calc can
  // never make the side income REDUCE tax, but rounding within the engine
  // could in theory produce a tiny negative — guard it.
  const extraTax = Math.max(0, combined.tax - baseline.tax);

  const effectiveRate =
    input.abnIncome > 0 ? extraTax / input.abnIncome : null;
  // Round the effective rate UP to the next clean step (conservative). 0 side
  // income → 0 set-aside.
  const setAsidePct =
    effectiveRate === null || effectiveRate <= 0
      ? 0
      : Math.ceil(effectiveRate / SET_ASIDE_STEP) * SET_ASIDE_STEP;

  // --- PAYG-instalment auto-entry flag (cited rule, not a recommendation). ---
  const { instalmentIncomeThreshold, taxOnNoaThreshold } =
    rules.paygInstalments;
  // Income leg: instalment income (gross business + investment income, excl GST
  // and capital gains). We use the expected ABN income as the instalment-income
  // proxy — it is the user's business income for the year.
  const incomeLegMet = input.abnIncome >= instalmentIncomeThreshold;
  // Tax leg: tax payable on the latest NoA ≥ $1,000. Prefer the prior-year NoA
  // when supplied; otherwise fall back to this year's estimated total tax as a
  // proxy (and flag that we did).
  const usedPriorYearNoa = input.priorYearTaxOnNoa !== undefined;
  const taxForLeg = usedPriorYearNoa
    ? (input.priorYearTaxOnNoa as number)
    : combined.tax;
  const taxLegMet = taxForLeg >= taxOnNoaThreshold;

  // --- GST trip-wire flag (side income alone vs the $75k threshold). ---
  const gstThreshold = rules.gst.turnoverThreshold;
  const gstLikely = input.abnIncome >= gstThreshold;

  return {
    salary: input.salary,
    abnIncome: input.abnIncome,
    taxOnSalary: baseline.tax,
    taxOnCombined: combined.tax,
    extraTax,
    marginalRate: combined.marginalRate,
    setAsidePct,
    effectiveRate,
    flags: {
      paygInstalments: {
        likely: incomeLegMet && taxLegMet,
        incomeLegMet,
        taxLegMet,
        usedPriorYearNoa,
      },
      gstRegistration: {
        likely: gstLikely,
        threshold: gstThreshold,
      },
    },
  };
}

/**
 * GST threshold: the $75k rolling 12-month registration test (current OR
 * projected), the 21-day deadline, the retrospective-GST warning, and the
 * neutral voluntary-registration trade-off. Pure.
 */
export function calculateGstThreshold(
  rawInput: GstThresholdInput,
  rules: SideIncomeRuleSet,
): GstThresholdOutput {
  const input = GstThresholdInputSchema.parse(rawInput);

  const sum = (xs: readonly number[]) => xs.reduce((a, b) => a + b, 0);
  const currentTurnover =
    input.currentTurnover ?? sum(input.monthlyTurnover as number[]);
  const projectedTurnover =
    input.projectedTurnover ?? sum(input.projectedMonthlyTurnover as number[]);

  const { turnoverThreshold, registrationDeadlineDays, gstRate, gstInclusiveDivisor } =
    rules.gst;

  // ATO: must register if GST turnover is AT OR ABOVE $75,000 (≥, "$75,000 or
  // more").
  const currentMeets = currentTurnover >= turnoverThreshold;
  const projectedMeets = projectedTurnover >= turnoverThreshold;
  const registrationRequired = currentMeets || projectedMeets;

  // Retrospective sting bites when registration is already required by the
  // CURRENT (backward) leg — turnover has already crossed, so GST may be owed
  // on past sales. Surfaced as a cited factual risk, never a $ figure.
  const retrospectiveRisk = currentMeets;

  // Neutral voluntary trade-off (always computed; relevant under the threshold).
  // Input credits ≈ GST-inclusive purchases ÷ 11. priceAddOnPct is the +10% you
  // add to your EX-GST price; inclusivePriceSharePct is the 1/11 ≈ 9.1% of the
  // NEW inclusive price — kept distinct so the two bases are never blurred.
  const voluntary = {
    inputCreditEstimate: Math.round(
      input.annualBusinessPurchases / gstInclusiveDivisor,
    ),
    priceAddOnPct: gstRate,
    inclusivePriceSharePct: 1 / gstInclusiveDivisor,
  };

  return {
    currentTurnover,
    projectedTurnover,
    threshold: turnoverThreshold,
    currentMeets,
    projectedMeets,
    registrationRequired,
    registrationDeadlineDays,
    retrospectiveRisk,
    voluntary,
  };
}

/**
 * Rideshare GST: the ride-sourcing carve-out. Ride-sourcing is taxi travel for
 * GST → GST from the FIRST dollar, no $75k threshold. Food delivery / other gig
 * keep the $75k test. ANY ride-sourcing in a mixed activity forces GST across
 * the whole ABN. Pure.
 */
export function calculateRideshareGst(
  rawInput: RideshareGstInput,
  rules: SideIncomeRuleSet,
): RideshareGstOutput {
  const input = RideshareGstInputSchema.parse(rawInput);
  const threshold = rules.gst.turnoverThreshold;

  const hasOtherActivity = input.doesFoodDelivery || input.doesOtherGig;
  // Ride-sourcing → GST from the first dollar, no threshold.
  const gstFromFirstDollar = input.doesRideSourcing;
  // Mixed: ride-sourcing present alongside any other activity → GST captures
  // the whole ABN (delivery/other income too).
  const mixedActivityForcesGst = input.doesRideSourcing && hasOtherActivity;

  // The $75k test only matters on the NO-ride-sourcing path.
  const turnoverThresholdMet =
    !input.doesRideSourcing && input.expectedTurnover >= threshold;

  let gstRequired: boolean;
  let reason: RideshareGstOutput["reason"];
  if (input.doesRideSourcing) {
    gstRequired = true;
    reason = "ride-sourcing-from-first-dollar";
  } else if (turnoverThresholdMet) {
    gstRequired = true;
    reason = "turnover-threshold-met";
  } else {
    gstRequired = false;
    reason = "below-threshold-no-rideshare";
  }

  // SERR applies whenever the user is on any reportable platform (ride-sourcing
  // from 1 Jul 2023, food delivery / other from 1 Jul 2024). True if any gig
  // activity is performed at all.
  const serrReportingApplies =
    input.doesRideSourcing || input.doesFoodDelivery || input.doesOtherGig;

  return {
    gstRequired,
    reason,
    gstFromFirstDollar,
    mixedActivityForcesGst,
    thresholdIfNoRideSourcing: threshold,
    turnoverThresholdMet,
    serrReportingApplies,
  };
}
